import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Plus, Calendar, Clock, User, Video, Trash2, Info, CalendarOff, Link2, CalendarPlus } from 'lucide-react';
import MeetingCard from './MeetingCard';
import ShareMenu from './ShareMenu';
import { Loader, EmptyState, trackPointer } from './UI';
import { hasCustomImage } from '../utils/Utils';
import { downloadICS, DEFAULT_DURATION } from '../utils/calendar';

function MeetingsComponent({ meetings, userId, handleDelete, loading }) {
  const [showEventModal, setShowEventModal] = useState(false);

  // Tarjeta a la que saltar al abrir un enlace compartido (/meetings#meeting-<id>)
  const [anchorId, setAnchorId] = useState(() => window.location.hash.slice(1) || null);

  useEffect(() => {
    if (loading || !anchorId) return;
    const card = document.getElementById(anchorId);
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
    setAnchorId(null);
  }, [loading, anchorId, meetings]);

  const formatDate = (dateString) => {
    if (!dateString) return "Sin fecha";
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric", month: "long", day: "numeric"
    });
  };

  // Agrupamos por mes/año para que la lista se lea como una agenda
  const groupedMeetings = useMemo(() => {
    if (!meetings) return {};
    return [...meetings]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .reduce((groups, m) => {
        const monthYear = new Date(m.date).toLocaleDateString("es-ES", { month: "long", year: "numeric" });
        (groups[monthYear] = groups[monthYear] || []).push(m);
        return groups;
      }, {});
  }, [meetings]);

  const total = meetings?.length || 0;

  // El enlace de la reunión viaja dentro del evento de calendario
  const meetingUrl = (meeting) => {
    if (!meeting.urlMeeting) return undefined;
    return meeting.urlMeeting.startsWith('http')
      ? meeting.urlMeeting
      : `https://${meeting.urlMeeting}`;
  };

  // Enlace al meeting: la agenda anclada en su tarjeta
  const meetingLink = (meeting) => `${window.location.origin}/meetings#meeting-${meeting._id}`;

  // Fecha, hora y enlace de la sala para que el mensaje se entienda solo
  const shareLines = (meeting) => [
    formatDate(meeting.date) + (meeting.time ? ` · ${meeting.time}` : ''),
    meetingUrl(meeting) ? `Sala: ${meetingUrl(meeting)}` : null,
  ];

  const calendarData = (meeting) => ({
    uid: meeting._id,
    title: meeting.name,
    date: meeting.date,
    time: meeting.time,
    minutes: DEFAULT_DURATION.meeting,
    details: meeting.description,
    location: meetingUrl(meeting),
    url: meetingUrl(meeting),
  });

  return (
    <div className="dyd-page">
      <Container>
        <div className="dyd-page-head">
          <div>
            <span className="dyd-eyebrow">Reuniones online</span>
            <h1 style={{ marginTop: 16, marginBottom: 10 }}>
              Agenda <span className="dyd-gradient-text">del club</span>
            </h1>
          </div>
          <button className="dyd-btn dyd-btn-primary" onClick={() => setShowEventModal(true)}>
            <Plus size={17} strokeWidth={2.4} />
            Crear meeting
          </button>
        </div>

        <p className="dyd-lead" style={{ marginBottom: 22 }}>
          Explora los próximos meetings organizados por la comunidad. Están agrupados por mes para que no te pierdas ninguno.
        </p>

        <div className="dyd-note" style={{ marginBottom: 36 }}>
          <Info size={18} />
          <span>
            <strong>Recordatorio:</strong> cuando crees un meeting, compártelo también en el grupo
            de WhatsApp del club para que todos puedan verlo a tiempo.
          </span>
        </div>

        <MeetingCard isOpen={showEventModal} onClose={() => setShowEventModal(false)} />

        {loading ? (
          <Loader text="Cargando reuniones" />
        ) : total === 0 ? (
          <EmptyState
            icon={CalendarOff}
            title="No hay meetings programados"
            hint="Crea el primero y compártelo con el club."
          />
        ) : (
          Object.entries(groupedMeetings).map(([monthYear, meetingsInMonth]) => (
            <section key={monthYear} style={{ marginBottom: 54 }}>
              <div className="dyd-section-title">
                <h2 style={{ margin: 0, textTransform: 'capitalize' }}>{monthYear}</h2>
                <span className="dyd-count">{meetingsInMonth.length}</span>
              </div>

              <div className="dyd-grid">
                {meetingsInMonth.map((meeting, index) => (
                  <article
                    key={meeting._id}
                    id={`meeting-${meeting._id}`}
                    className="dyd-card dyd-rise"
                    style={{ '--i': index % 12 }}
                    onMouseMove={trackPointer}
                  >
                    {hasCustomImage(meeting.meetingImage) && (
                      <div className="dyd-card-media">
                        <img src={meeting.meetingImage} alt={meeting.name} />
                      </div>
                    )}

                    <div className="dyd-card-body">
                      {!hasCustomImage(meeting.meetingImage) && (
                        <span className="dyd-meeting-icon">
                          <Video size={20} strokeWidth={1.9} />
                        </span>
                      )}

                      <h3 className="dyd-card-title">{meeting.name}</h3>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span className="dyd-meta">
                          <Calendar size={14} /> {formatDate(meeting.date)}
                        </span>
                        {meeting.time && (
                          <span className="dyd-meta"><Clock size={14} /> {meeting.time}</span>
                        )}
                        <span className="dyd-meta">
                          <User size={14} /> {meeting.creator?.name || "Desconocido"}
                        </span>
                      </div>

                      <p className="dyd-card-text">
                        {meeting.description?.length > 120
                          ? meeting.description.substring(0, 120) + '…'
                          : meeting.description || "Sin descripción"}
                      </p>

                      <div className="dyd-card-actions">
                        {meeting.urlMeeting ? (
                          <a
                            className="dyd-btn dyd-btn-primary dyd-btn-block"
                            href={meetingUrl(meeting)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Link2 size={16} />
                            Entrar a la reunión
                          </a>
                        ) : (
                          <span className="dyd-hint" style={{ textAlign: 'center' }}>Sin enlace disponible</span>
                        )}

                        <button
                          className="dyd-btn dyd-btn-ghost dyd-btn-block"
                          onClick={() => downloadICS(calendarData(meeting))}
                          title="Descarga un .ics que abre tu calendario"
                        >
                          <CalendarPlus size={16} />
                          Añadir al calendario
                        </button>

                        <ShareMenu
                          title={meeting.name}
                          lines={shareLines(meeting)}
                          url={meetingLink(meeting)}
                        />

                        {userId === meeting.creator?.userId && (
                          <button
                            className="dyd-btn dyd-btn-danger dyd-btn-sm dyd-btn-block"
                            onClick={() => {
                              if (window.confirm('¿Estás seguro de que quieres eliminar este meeting?')) {
                                handleDelete(meeting._id);
                              }
                            }}
                          >
                            <Trash2 size={14} />
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))
        )}
      </Container>

      <style>{`
        .dyd-meeting-icon {
          display: grid;
          place-items: center;
          width: 44px;
          height: 44px;
          border-radius: 13px;
          color: var(--dyd-blue-light);
          background: var(--dyd-accent-grad-soft);
          border: 1px solid rgba(59,130,246,0.26);
          transition: transform 0.45s var(--dyd-ease), box-shadow 0.45s var(--dyd-ease);
        }
        .dyd-card:hover .dyd-meeting-icon {
          transform: translateY(-2px) rotate(-6deg);
          box-shadow: 0 10px 26px rgba(59,130,246,0.32);
        }
      `}</style>
    </div>
  );
}

export default MeetingsComponent;
